// ProctoringWarning — single overlay that surfaces real-time detector alerts.
//
// Detection events get POSTed to the backend AND surface here as a brief
// in-app warning so the candidate can self-correct. Industry data shows
// real-time warnings reduce false-positive flags dramatically — most
// "no face" episodes are candidates stretching / grabbing water, and a
// nudge brings them back without a permanent flag.
//
// Design:
//   * One overlay component, dismisses warnings on a stack.
//   * Newest warning replaces older ones to avoid clutter.
//   * Severity tints: yellow for warn, red for flag, dark-red for critical.
//   * Auto-dismisses after AUTO_DISMISS_MS.
//   * Top-right corner so it doesn't overlap the code editor.
//
// Props:
//   warning: { feature_key, severity, message, evidence } | null
//   onDismiss: () => void

import React, { useEffect } from 'react';
import { AlertTriangle, Eye, Camera, Mic, Hand } from 'lucide-react';

const AUTO_DISMISS_MS = 6000;

// Icon per feature group — chosen at a glance so the candidate immediately
// knows WHAT's being warned about, not just "something is wrong".
const FEATURE_ICONS = {
  'mediapipe.face_presence': Camera,
  'mediapipe.multiple_faces': Eye,
  'mediapipe.head_pose': Eye,
  'mediapipe.eye_gaze': Eye,
  'mediapipe.hand_tracking': Hand,
  'mediapipe.audio_classifier': Mic,
};

const SEVERITY_STYLES = {
  info:     'bg-blue-50 border-blue-300 text-blue-900',
  low:      'bg-amber-50 border-amber-300 text-amber-900',
  medium:   'bg-amber-100 border-amber-400 text-amber-900',
  high:     'bg-red-50 border-red-400 text-red-900',
  critical: 'bg-red-100 border-red-600 text-red-900',
};

export default function ProctoringWarning({ warning, onDismiss }) {
  // Auto-dismiss timer — re-arms whenever a new warning arrives so a
  // freshly-issued warning gets the full AUTO_DISMISS_MS window even if
  // an older one was about to fade.
  useEffect(() => {
    if (!warning) return undefined;
    const timer = setTimeout(() => {
      onDismiss && onDismiss();
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [warning, onDismiss]);

  if (!warning) return null;

  const Icon = FEATURE_ICONS[warning.feature_key] || AlertTriangle;
  const styleClass =
    SEVERITY_STYLES[warning.severity] || SEVERITY_STYLES.medium;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={[
        'fixed top-4 right-4 z-[10000]',
        'max-w-sm border-2 rounded-lg shadow-lg p-3',
        'flex items-start gap-3',
        'transition-opacity duration-200',
        styleClass,
      ].join(' ')}
    >
      <Icon className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight">
          {warning.message || 'Proctoring alert'}
        </div>
        {warning.evidence?.stage === 'flag' && (
          <div className="text-xs mt-1 opacity-80">
            This has been recorded. Repeated alerts may flag your test for review.
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss && onDismiss()}
        className="text-xs underline opacity-70 hover:opacity-100 shrink-0"
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}
